// SPDX-License-Identifier: Elastic-2.0
/**
 * NewRoutineStub — placeholder for `/routines/new` (T-026).
 *
 * Per functional spec § 13 the full automation editor is out of scope at
 * v0.1 (deferred to Wave 8). This stub is the landing surface for the
 * "Add to routine" CTA from DeviceModal, which navigates here with
 * `?prefill=device:{id}` so we can frame the placeholder around the
 * device the user came from.
 *
 * Two render branches:
 *   1. `?prefill=device:{id}` present → render an informational card
 *      with the device name (looked up from the cached devices list)
 *      and two CTAs: "Back" (navigate -1) and a disabled "routine builder
 *      coming soon" control. (F-357 / ADR-010 removed the former `/ha`
 *      escape to Home Assistant — no HA UI surface ships.)
 *   2. No prefill → emit a toast explaining the editor is coming later
 *      and redirect to `/routines`. We use `<Navigate replace>` so the
 *      stub doesn't pollute the history stack — back from the list
 *      takes the user one step further back, as expected.
 *
 * Why we resolve the device from the QueryClient cache instead of
 * triggering a fresh `useDevice(id)` fetch: the user just came from the
 * DeviceModal which already populated the list cache. If the cache miss
 * happens (deep link, refresh), the helper degrades gracefully and we
 * just show the raw id in the title — the page is informational, not
 * functional.
 */
import { useEffect, useMemo, useRef, type ReactElement } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/Button";
import { Page } from "@/components/Page/Page";
import { useToast } from "@/components/Toast/useToast";
import { ROUTES } from "@/routing/routes";
import { DEVICES_QUERY_KEY } from "@/features/dashboard/useDevices";

import type { DeviceListResponse } from "@/api/gateway/models/DeviceListResponse";

import { parsePrefill } from "./parsePrefill";
import styles from "./RoutinesListPage.module.css";

export function NewRoutineStub(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [searchParams] = useSearchParams();
  const prefill = useMemo(
    () => parsePrefill(searchParams.get("prefill")),
    [searchParams],
  );

  // Guard the redirect-side toast so React StrictMode's double-mount
  // doesn't enqueue it twice in development.
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (prefill !== null) return;
    if (toastFiredRef.current) return;
    toastFiredRef.current = true;
    showToast({
      kind: "info",
      message: t("routines.new.redirect_toast"),
    });
  }, [prefill, showToast, t]);

  if (prefill === null) {
    return <Navigate to={ROUTES.ROUTINES} replace data-testid="routines-new-redirect" />;
  }

  // Best-effort device-name lookup from the cached list (populated by
  // `useDevices()` on the dashboard). Falls back to the raw id when the
  // cache is cold (e.g. deep link or refresh).
  const cachedList = queryClient.getQueryData<DeviceListResponse>(DEVICES_QUERY_KEY);
  const device = cachedList?.devices.find((d) => d.id === prefill.id);
  const deviceName = device?.name ?? prefill.id;

  return (
    <Page title={t("routines.page_title")} data-testid="routines-new-page">
      <div className={styles.stub} data-testid="routines-new-stub">
        <h2 className={styles.stubTitle} data-testid="routines-new-title">
          {t("routines.new.prefill_title", { deviceName })}
        </h2>
        <p className={styles.stubIntro}>{t("routines.new.prefill_intro")}</p>
        <div className={styles.stubActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="routines-new-back"
          >
            {t("routines.new.back")}
          </Button>
          {/* F-357 / ADR-010: no /ha escape to Home Assistant. The full
             Mirlai routine builder is deferred (former ADR-004 Wave 8) — show
             an honest disabled "coming soon" control, not a link to HA. */}
          <Button
            variant="secondary"
            size="sm"
            disabled
            data-testid="routines-new-builder-coming-soon"
          >
            {t("routines.new.builder_coming_soon")}
          </Button>
        </div>
      </div>
    </Page>
  );
}

export default NewRoutineStub;
